import { useEffect, useState } from 'react';

import { ExternalLink, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import PartyTabSection from '@/components/common/PartyTabSection';
import PlatformLink from '@/components/common/PlatformLink';
import SubmitButton from '@/components/common/SubmitButton';
import { Button } from '@/components/shadcn/button';
import { Input } from '@/components/shadcn/input';
import useSelectedParty from '@/features/analysis/hooks/useSelectedParty';
import { toDateString } from '@/lib/utils/date';
import hasItems from '@/lib/utils/hasItems';

import useMyEnvironment from '../../hooks/useMyEnvironment';

export default function MyEnvironmentSection() {
  const { t } = useTranslation();
  const [inviteEmail, setInviteEmail] = useState('');

  const party = useSelectedParty();

  useEffect(() => {
    if (!party?.email || inviteEmail !== '') return;

    setInviteEmail(party.email);
  }, [inviteEmail, party?.email]);

  const { customers, isLoading, hasAccess, inviteUser } = useMyEnvironment();

  const handleInvite = () =>
    inviteUser({
      partyId: party?.id ?? '',
      email: inviteEmail,
    });

  if (!hasAccess) return null;

  return (
    <PartyTabSection
      icon={User}
      title={t('MIJN_OMGEVING')}
      isLoading={isLoading}
      defaultCollapsed
    >
      {!hasItems(customers) && (
        <div className="flex flex-col gap-4">
          <span className="text-sm">{t('NO_LINKED_USER_DESC')}</span>

          <div className="flex gap-2">
            <Input
              id="email"
              name="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
            />

            <SubmitButton onSubmit={handleInvite}>{t('INVITE')}</SubmitButton>
          </div>
        </div>
      )}

      {hasItems(customers) && (
        <div className="flex flex-col gap-2">
          <p className="text-muted-foreground text-xs">
            {t('MY_ENVIRONMENT_SECTION_DESCRIPTION')}
          </p>

          <div className="flex flex-col gap-2">
            {customers.map((customer) => (
              <div
                key={customer.id}
                className="flex flex-col rounded-md border p-2"
              >
                <span className="text-sm">{customer.email}</span>
                <span className="text-muted-foreground text-xs">
                  {t('LAST_LOGIN')}
                  {': '}
                  {toDateString(customer.last_login) ?? '-'}
                </span>
              </div>
            ))}
          </div>

          <div className="flex justify-end">
            <Button variant="ghost" size="xs" asChild>
              <PlatformLink
                to={`/parties/${party?.id ?? ''}/customer-portal`}
                target="_blank"
              >
                {t('MORE_ACTIONS')} <ExternalLink className="size-3.5" />
              </PlatformLink>
            </Button>
          </div>
        </div>
      )}
    </PartyTabSection>
  );
}
